import deleteCompanyOptionApi from '@/api/delete/company/delete-company-option';
import getCompanyOptionsApi from '@/api/get/company/company-options/get-company-options';
import deleteBulkCompanyOptionApi from '@/api/post/company/company-option/delete-bulk-company-option';
import Alert from '@/components/alert/alert-component';
import { PrimaryOutlineButton } from '@/components/button/button-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import PermissionWrapper from '@/components/permission-wrapper/permission-wrapper-component';
import StatusIndicator from '@/components/status-indicator/status-indicator-component';
import TableRowLogo from '@/components/table/components/logo/table-row-logo.component';
import Checkbox from '@/components/table/components/table-checkbox/table-checkbox-component';
import ViewEditDelete from '@/components/table/components/view-edit-delete/view-edit-delete';
import Table from '@/components/table/table';
import Tooltip from '@/components/tooltip/tooltip-component';
import { useLazyQuery, useMutation } from '@/hooks/use-api';
import { Subtitle3Text } from '@/utils/global-fonts';
import { useCallback, useRef, useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { MdDelete } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import Styled from './options-all-style';

const CheckboxStyled = {
  bottom: 10,
  paddingLeft: '0px',
};
const multiSelectHeaderButtonStyle = {
  padding: '10px',
  margin: '4px',
  backgroundColor: 'transparent',
  color: 'White',
};

const buttonText = {
  color: 'White',
};

const renderStatus = ({ row }) => (
  <StatusIndicator status={row.original.status.toLowerCase()} />
);
/**
 * Page that shows all Options in a table format.
 * @returns {JSX.Element} The rendered Options Management page component.
 */
const OptionsAll = () => {
  const tableId = 'options-list';
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedRows, setSelectedRows] = useState([]);
  const [fetchingPresets, setFetchingPresets] = useState(true);
  const [defaultVariables, setDefaultVariables] = useState({});
  const [defaultSort, setDefaultSort] = useState('created_at:DESC');
  const renderLogoAndUniqueId = ({ row }) => {
    const thumbnailUrl = row?.original?.thumbnail
      ? `${row?.original?.thumbnail}`
      : '';
    return thumbnailUrl && <TableRowLogo src={thumbnailUrl} />;
  };

  const getApi = useRef(getCompanyOptionsApi(id));

  const {
    data,
    count,
    error,
    loading: isLoading,
    refetch,
  } = useLazyQuery(getApi.current);

  const [deleteCompanyOption, { loading: deleteCompanyOptionLoading }] =
    useMutation(deleteCompanyOptionApi);
  const [deleteBulkCompanyOption, { loading: deleteBulkCompanyOptionLoading }] =
    useMutation(deleteBulkCompanyOptionApi);

  const getTablePresets = useCallback(() => {
    const tablePreferences = localStorage.getItem('tablePreferences');
    if (tablePreferences) {
      const tableConfigs = JSON.parse(tablePreferences)[tableId];

      if (tableConfigs) {
        const { query } = tableConfigs;

        if (query.length > 0) {
          // Get cached search value and apply it
          const searchQuery = query?.filter((q) => q.includes('search='));
          if (searchQuery?.length > 0) {
            const tempSearch = searchQuery[0].replace('search=', '');
            setDefaultVariables({ ...defaultVariables, search: tempSearch });
          }

          // Get cached sort value and apply it
          const sortQuery = query?.filter((q) => q.includes('sort='));
          if (sortQuery?.length > 0) {
            const tempSort = sortQuery[0].replace('sort=', '');
            setDefaultSort(tempSort);
          }

          // Get cached page size and apply it
          const pageSizeQuery = query?.filter((q) => q.includes('limit='));
          if (pageSizeQuery?.length > 0) {
            const tempPageSize = parseInt(
              pageSizeQuery[0].replace('limit=', ''),
              10,
            );
            setDefaultVariables({ ...defaultVariables, limit: tempPageSize });
          }
        }
      }
    }
    setFetchingPresets(false);

    // This is disabled because we don't want to re-run this function when the defaultVariables changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // USE EFFECTS
  useEffect(() => {
    getTablePresets();
  }, [getTablePresets]);

  const launchDeleteConfirmation = useCallback(
    (OptionData) => {
      const message =
        OptionData.original?.hasQuote || OptionData.original?.hasProduct
          ? 'Are you sure you want to delete this option as it will be unlinked from Products?'
          : 'Are you sure you want to delete this option?';

      ConfirmationModal({
        title: 'Delete Option',
        text: message,
        onConfirm: () =>
          deleteCompanyOption(id, OptionData?.original?.id).then(() => {
            // TODO: Remove this once we have a proper way to handle sorting (Milestone 3)
            refetch('sort=created_at:DESC').then(() => {
              Alert({
                icon: 'success',
                text: 'Option deleted successfully',
              });
            });
          }),
      });
    },
    [deleteCompanyOption, id, refetch],
  );

  const handleRowClick = useCallback(
    (clickedRow) => {
      const clickedRowId = clickedRow;
      setSelectedRows((prevSelectedRow) => {
        const isSelected = prevSelectedRow.some((row) => row === clickedRowId);

        if (isSelected) {
          // If selected, remove it
          return prevSelectedRow.filter((row) => row !== clickedRowId);
        }

        // If not selected, add it
        return [...prevSelectedRow, clickedRow];
      });
    },
    [setSelectedRows],
  );

  const renderRowCheckbox = useCallback(
    (value) => (
      <Checkbox
        value={selectedRows?.some((item) => item === value.id)}
        width="auto"
        onChange={() => {
          handleRowClick(value.id);
        }}
        style={CheckboxStyled}
      />
    ),
    [handleRowClick, selectedRows],
  );

  const renderHeaderCheckbox = useCallback(
    (value) => (
      <Checkbox
        width="auto"
        value={selectedRows.length === value.data.length}
        onChange={() => {
          value.data.map((row) => handleRowClick(row.id));
        }}
        style={CheckboxStyled}
      />
    ),
    [handleRowClick, selectedRows.length],
  );

  const handleEdit = useCallback(
    (row) => {
      const optionsDetails = row.original;
      navigate(`edit/${optionsDetails.id}`);
    },
    [navigate],
  );
  const handleView = useCallback(
    (row) => {
      const optionsDetails = row.original;
      navigate(`view/${optionsDetails.id}`);
    },
    [navigate],
  );

  const handleDuplicate = useCallback(
    (row) => {
      const optionsDetails = row.original;
      navigate(`new`, {
        state: { isOptionDuplicate: optionsDetails.id },
      });
    },
    [navigate],
  );

  const handleDelete = useCallback(
    (row) => {
      launchDeleteConfirmation(row);
    },
    [launchDeleteConfirmation],
  );

  const Actions = useCallback(
    ({ row }) => (
      <ViewEditDelete
        feature="option"
        onEdit={() => handleEdit(row)}
        onView={() => handleView(row)}
        onDelete={() => handleDelete(row)}
        onDuplicate={() => handleDuplicate(row)}
      />
    ),
    [handleDelete, handleDuplicate, handleEdit, handleView],
  );

  const renderPartNumber = useCallback((value) => {
    const { id: optionId, parts = [] } = value;

    const partElements = parts.map((part) => {
      const { part_number: partNumber, temp_pn: tempPn } = part;

      const textContent = (
        <Subtitle3Text primary={!tempPn}>{partNumber}</Subtitle3Text>
      );

      if (!tempPn) {
        return (
          <span key={`part-number-${optionId}-${partNumber}`}>
            {textContent}
          </span>
        );
      }

      return (
        <Tooltip
          key={`tooltip-${optionId}-${partNumber}`}
          id={`temp-pn-${optionId}-${partNumber}`}
          text={textContent}
          toolTipText="Temporary Part Number"
        />
      );
    });

    return (
      <div>
        {partElements.map((element, index) => (
          <span
            key={`part-number-element-${optionId}-${element.key + index}-${parts?.[index]?.price_id}`}
          >
            {element}
            {index < partElements.length - 1 && ', '}
          </span>
        ))}
      </div>
    );
  }, []);

  const OptionsColumns = [
    {
      Header: (props) => renderHeaderCheckbox(props),
      id: 'isChecked',
      accessor: 'isChecked',
      width: '10px',
      disableSortBy: true,
      Cell: ({ row }) => renderRowCheckbox(row.original),
    },
    {
      Header: 'IMAGE',
      accessor: 'id',
      search: true,
      id: 'id',
      width: '40px',
      Cell: renderLogoAndUniqueId,
      inputSort: true,
    },
    {
      Header: 'NAME',
      id: 'name',
      accessor: 'name',
      search: true,
    },
    {
      Header: 'PART NUMBER',
      id: 'parts',
      accessor: 'parts',
      search: true,
      Cell: ({ row }) => renderPartNumber(row.original),
    },
    {
      Header: 'STATUS',
      id: 'status',
      accessor: 'status',
      search: true,
      Cell: (props) => renderStatus(props),
    },
    {
      Header: 'ACTIONS',
      id: 'actions',
      Cell: (props) => Actions(props),
    },
  ];

  const handleBulkDelete = useCallback(() => {
    deleteBulkCompanyOption(id, selectedRows).then(() => {
      // TODO: Remove this once we have a proper way to handle sorting (Milestone 3)
      refetch('sort=created_at:DESC');
      setSelectedRows([]);
    });
  }, [id, selectedRows, deleteBulkCompanyOption, refetch]);

  return (
    <>
      {selectedRows.length > 0 && (
        <Styled.StyledMainRow>
          <Styled.StyledDiv>
            <Checkbox
              value
              textColor="#fff"
              color="#ED1C24"
              textStyle
              label={`${selectedRows.length} items Selected`}
              onChange={() => {
                setSelectedRows([]);
              }}
            />
          </Styled.StyledDiv>
          <Styled.StyledDiv>
            <PermissionWrapper permissions={['option.delete']}>
              <PrimaryOutlineButton
                title="Delete"
                buttonHeight="40px"
                textStyle={buttonText}
                buttonWidth="fit-content"
                icon={<MdDelete />}
                style={multiSelectHeaderButtonStyle}
                cy="btnBulkOptionsDelete"
                onClick={handleBulkDelete}
              />
            </PermissionWrapper>
            <Styled.StepDivider />
            <PrimaryOutlineButton
              title="Cancel"
              buttonHeight="40px"
              buttonWidth="fit-content"
              textStyle={buttonText}
              icon={<FiX />}
              style={multiSelectHeaderButtonStyle}
              cy="btnCancelBulkOptionsSelect"
              onClick={() => {
                setSelectedRows([]);
              }}
            />
          </Styled.StyledDiv>
        </Styled.StyledMainRow>
      )}
      <Table
        feature="option"
        onRowEdit={(row) => handleEdit(row)}
        onRowDelete={(row) => handleDelete(row)}
        onRowDuplicate={(row) => handleDuplicate(row)}
        onRowView={(row) => handleView(row)}
        columns={OptionsColumns}
        data={data ?? []}
        tableOptions={['search', 'columns']}
        id={tableId}
        dataCount={count ?? 0}
        error={error}
        defaultVariables={defaultVariables}
        defaultSort={defaultSort}
        loading={
          fetchingPresets ||
          isLoading ||
          deleteCompanyOptionLoading ||
          deleteBulkCompanyOptionLoading
        }
        filters={[]}
        tableHeader={selectedRows.length < 1}
        refetch={refetch}
        tableHeaderProps={
          <PermissionWrapper permissions={['option.create']}>
            <Styled.HeaderButtonStyled
              title="Add Option"
              buttonHeight="40px"
              buttonWidth="124px"
              margin="0 0 0 6px"
              cy="btnCreateOption"
              onClick={() => {
                navigate('new');
              }}
            />
          </PermissionWrapper>
        }
      />
    </>
  );
};

export default OptionsAll;
